// react-router-dom components
import React from 'react';
import { Link } from 'react-router-dom';
// @mui material components
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import Tooltip from '@mui/material/Tooltip';
// prop-types is a library for typechecking of props
import PropTypes from 'prop-types';

import MDAvatar from 'components/MDAvatar';
// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

function DefaultProjectCard({
  image,
  label,
  title,
  description,
  action,
  authors,
}) {
  const renderAuthors = authors.map(({ image: media, name }) => (
    <Tooltip key={name} title={name} placement='bottom'>
      <MDAvatar
        src={media}
        alt={name}
        size='xs'
        sx={({ borders: { borderWidth }, palette: { white } }) => ({
          border: `${borderWidth[2]} solid ${white.main}`,
          cursor: 'pointer',
          position: 'relative',
          ml: -1.25,

          '&:hover, &:focus': {
            zIndex: '10',
          },
        })}
      />
    </Tooltip>
  ));

  return (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'transparent',
        boxShadow: 'none',
        overflow: 'visible',
        padding: '10px',
        cursor: 'pointer',
        transition: 'box-shadow 0.3s ease-in-out', // Add transition for smooth hover effect
        '&:hover': {
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.4)',
        },
      }}
    >
      <MDBox lineHeight={0}>
        <MDTypography variant='h6' color='text'>
          Before & after
        </MDTypography>
      </MDBox>
      <MDBox position='relative' width='100.25%' shadow='xl' borderRadius='xl'>
        <CardMedia
          src={image}
          component='img'
          title={title}
          sx={{
            maxWidth: '100%',
            margin: 0,
            boxShadow: ({ boxShadows: { md } }) => md,
            objectFit: 'cover',
            objectPosition: 'center',
          }}
        />
      </MDBox>
      <MDBox mt={1} mx={0.5}>
        <MDTypography
          variant='button'
          fontWeight='regular'
          color='text'
          textTransform='capitalize'
        >
          {label}
        </MDTypography>
        <MDBox mb={1}>
          {action.type === 'internal' ? (
            <MDTypography
              component={Link}
              to={action.route}
              variant='h5'
              textTransform='capitalize'
            >
              {title}
            </MDTypography>
          ) : (
            <MDTypography
              component='a'
              href={action.route}
              target='_blank'
              rel='noreferrer'
              variant='h5'
              textTransform='capitalize'
            >
              {title}
            </MDTypography>
          )}
        </MDBox>
        <MDBox mb={3} lineHeight={0}>
          <MDTypography variant='button' fontWeight='light' color='text'>
            {description}
          </MDTypography>
        </MDBox>
        <MDBox
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <MDBox display='flex'>{renderAuthors}</MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of DefaultProjectCard
DefaultProjectCard.defaultProps = {
  authors: [],
};

// Typechecking props for the DefaultProjectCard
DefaultProjectCard.propTypes = {
  image: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  action: PropTypes.shape({
    type: PropTypes.oneOf(['external', 'internal']),
    route: PropTypes.string.isRequired,
    color: PropTypes.oneOf([
      'primary',
      'secondary',
      'info',
      'success',
      'warning',
      'error',
      'light',
      'dark',
      'white',
    ]).isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  authors: PropTypes.arrayOf(PropTypes.object),
};

export default DefaultProjectCard;
